import { Component, OnInit, ViewChild, inject } from '@angular/core';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';
import { HttpErrorResponse } from '@angular/common/http';
import { ActivatedRoute, Router } from '@angular/router';
import { AppModelModalComponent } from '@nimmsta/ng-commons-lib/forms';
import {
  Column,
  CustomAction,
  DataTableComponent,
  EmptyTableComponent,
  NimmstaDataSource,
} from '@nimmsta/ng-commons-lib/data-table';
import { DateTimePipe, StatusBadgePipe } from '@nimmsta/ng-commons-lib/pipes';
import { ErrorComponent } from '@nimmsta/ng-commons-lib/error';
import { HeaderBarComponent } from '@nimmsta/ng-commons-lib/header-bar';
import {
  Organization,
  OrganizationStatus,
} from '@nimmsta/ng-commons-lib/models';
import { OrganizationService } from '@nimmsta/ng-commons-lib/services/organization';
import { OrganizationFormComponent } from './organization-form.component';
import { ApproveDeclineAction } from './approveDeclineAction';
import { OrganizationTypePipe } from './organization-type.pipe';

@Component({
  selector: 'app-organizations',
  templateUrl: './organizations.component.html',
  imports: [
    OrganizationFormComponent,
    TranslatePipe,
    HeaderBarComponent,
    DataTableComponent,
    EmptyTableComponent,
    ErrorComponent,
    AppModelModalComponent,
  ],
  providers: [StatusBadgePipe, OrganizationTypePipe, DateTimePipe],
})
export class OrganizationsComponent implements OnInit {
  private organizationService = inject(OrganizationService);
  private translate = inject(TranslateService);
  private statusBadge = inject(StatusBadgePipe);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private organizationTypePipe = inject(OrganizationTypePipe);
  private dateTimePipe = inject(DateTimePipe);

  organizationSource!: NimmstaDataSource<Organization>;

  @ViewChild(DataTableComponent, { static: true })
  private tableComponent!: DataTableComponent<Organization>;

  @ViewChild('EditModal', { static: true })
  private editModalComponent!: AppModelModalComponent<Organization>;

  @ViewChild(OrganizationFormComponent, { static: true })
  private orgFormComponent!: OrganizationFormComponent;

  get columns(): Column[] {
    return [
      new Column('name', 'organizations.name', { sortable: true }),
      new Column('description', 'organizations.description', {
        sortable: true,
      }),
      new Column('address', 'organizations.address', { sortable: true }),
      new Column('type', 'organizations.type', {
        sortable: true,
        pipe: this.organizationTypePipe,
      }),
      new Column(
        'parentOrganization.name',
        'organizations.parentOrganization',
        { sortable: true, sortingName: 'parentOrganization.name' },
      ),
      new Column(
        'motherOrganization.name',
        'organizations.motherOrganization',
        { sortable: true, sortingName: 'motherOrganization.name' },
      ),
      new Column('status', 'organizations.status', {
        pipe: this.statusBadge,
        sortable: true,
      }),
      new Column('createdAt', 'createdAt', {
        pipe: this.dateTimePipe,
        sortable: true,
      }),
    ];
  }

  customActions: CustomAction<Organization>[] = [];

  error?: HttpErrorResponse;

  ngOnInit(): void {
    this.organizationSource = new NimmstaDataSource<Organization>(
      this.organizationService,
    );
    const approveDeclineAction = new ApproveDeclineAction(
      'organizations.approve',
    );
    this.customActions.push(approveDeclineAction);
    approveDeclineAction.subscribe((id) => this.approveDecline(id));
    const editAction = new CustomAction('edit');
    this.customActions.push(editAction);
    editAction.subscribe((id) => this.edit(id));
    const deleteAction = new CustomAction('delete', false, true);
    this.customActions.push(deleteAction);
    deleteAction.subscribe((id) => this.delete(id));
  }

  approveDecline(id: string) {
    const org = this.getSelectedObject(id)!;
    if (org.status === OrganizationStatus.ACTIVE) {
      this.decline(org);
    } else {
      this.approve(org);
    }
  }

  approve(org: Organization) {
    this.toggleStatus(org, OrganizationStatus.ACTIVE);
  }

  decline(org: Organization) {
    this.toggleStatus(org, OrganizationStatus.INACTIVE);
  }

  private toggleStatus(org: Organization, status: OrganizationStatus) {
    org.status = status;
    this.organizationService.createOrUpdate(org).subscribe({
      next: () => {
        this.tableComponent.loadDataPage();
      },
      error: (error: HttpErrorResponse) => {
        this.error = error;
      },
    });
  }

  new() {
    this.translate.get('organizations.new').subscribe((value) => {
      this.openModal(value, true, new Organization());
    });
  }

  edit(id: string) {
    this.translate.get('organizations.edit').subscribe((value) => {
      this.openModal(value, true, this.getSelectedObject(id)!);
    });
  }

  delete(id: string) {
    this.organizationService.delete(this.getSelectedObject(id)!).subscribe({
      next: () => {
        this.tableComponent.loadDataPage();
      },
      error: (error: HttpErrorResponse) => {
        this.error = error;
      },
    });
  }

  show(id: string) {
    this.router.navigate([id], { relativeTo: this.route });
  }

  private getSelectedObject(id: string): Organization | undefined {
    return this.organizationSource.data?.content
      .filter((d) => id === d.id)
      .pop();
  }

  onSave(): void {
    this.orgFormComponent.onSave();
  }

  onModalClose(): void {
    this.editModalComponent.hide();
    this.tableComponent.loadDataPage();
  }

  onLoading(loading: boolean): void {
    this.editModalComponent.loading = loading;
  }

  private openModal(
    title: string,
    editable: boolean,
    organization: Organization,
  ): void {
    this.orgFormComponent.editable = editable;
    this.orgFormComponent.setValue(organization);
    this.orgFormComponent.loadOrganizations();
    this.editModalComponent.title = title;
    this.editModalComponent.editable = editable;
    this.editModalComponent.show();
  }
}
